import React from 'react';
import { CheckIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { CtaBand } from '../components/CtaBand';
import { howWeWorkItems } from '../data/content';
import { usePageTitle } from '../hooks/usePageTitle';
export function About() {
  usePageTitle('About | Viridia Analytics');
  return <>
      <PageHeader eyebrow="About" title="We build response systems for businesses that pay to make the phone ring." body="Viridia Analytics is a Phoenix-based company founded by Kevin Rogers." data-id="element-358" data-mp="2ej859j9l10n4" />

      <section aria-labelledby="why-title" className="border-b border-white/5 bg-ink-800" data-id="element-359" data-mp="3751cq65tp3g8">
        <div className="mx-auto max-w-content px-5 py-20 sm:px-8 sm:py-24" data-id="element-360" data-mp="2x8y189p2zsgm">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand" data-id="element-361" data-mp="2xbapt5pch12n">
            Our reason
          </p>
          <h2 id="why-title" className="mt-5 max-w-2xl font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-362" data-mp="1ltsy54l1frwh">
            Why Viridia exists.
          </h2>
          <div className="mt-8 max-w-3xl space-y-5 text-base leading-relaxed text-white/70 sm:text-lg" data-id="element-363" data-mp="1pjgx2y28iswa">
            <p data-id="element-364" data-mp="23jj8c1g1eo2k">
              Viridia Analytics is a Phoenix-based company founded by Kevin Rogers that
              builds response systems for lead-driven businesses.
            </p>
            <p data-id="element-365" data-mp="2dfunr0axbu18">
              The story is simple. Lead-driven businesses pay real money to make the phone
              ring and then lose the race to answer it. We started in home services, where
              a missed call costs a job, and built the system that answers every lead in
              seconds and books it.
            </p>
          </div>
        </div>
      </section>

      <section aria-labelledby="results-title" className="border-b border-white/5 bg-ink-900" data-id="element-366" data-mp="2w6vvacq40xwe">
        <div className="mx-auto max-w-content px-5 py-20 sm:px-8 sm:py-24" data-id="element-367" data-mp="2r8u3n3embq9b">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand" data-id="element-368" data-mp="1ouwi1oyzw5uz">
            Measured
          </p>
          <h2 id="results-title" className="mt-5 max-w-2xl font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-369" data-mp="1svgh4y34pjjr">
            The results that sent us here.
          </h2>

          <figure className="mt-12 rounded-xl border border-white/10 bg-ink-800 p-7 sm:p-10" data-id="element-370" data-mp="22ncg0bd1hdp1">
            <figcaption className="text-sm font-semibold uppercase tracking-[0.14em] text-white/45" data-id="element-371" data-mp="27kupc2uyxuxk">
              Home services client, same response-time system:
            </figcaption>
            <div className="mt-8 grid gap-8 sm:grid-cols-2 sm:gap-10" data-id="element-372" data-mp="2gmc33ekwlkj3">
              <div className="border-t border-white/10 pt-6" data-id="element-373" data-mp="2ll5jberkrpmy">
                <p className="font-display text-3xl font-bold leading-tight tracking-tight text-brand sm:text-4xl" data-id="element-374" data-mp="2lij7v41kcpzg">
                  4+ hours to under 10 minutes
                </p>
                <p className="mt-3 text-sm text-white/60" data-id="element-375" data-mp="r8yuve9o7ccp">Average lead response time</p>
              </div>
              <div className="border-t border-white/10 pt-6" data-id="element-376" data-mp="jp6dox58a5nc">
                <p className="font-display text-3xl font-bold leading-tight tracking-tight text-brand sm:text-4xl" data-id="element-377" data-mp="evv5arkrpgtb">
                  Roughly 39%
                </p>
                <p className="mt-3 text-sm text-white/60" data-id="element-378" data-mp="4yz5da8xq30d">
                  Close rate on booked appointments
                </p>
              </div>
            </div>
          </figure>

          <p className="mt-10 max-w-3xl text-base leading-relaxed text-white/70 sm:text-lg" data-id="element-379" data-mp="xn4ndp8ltdwi">
            Real estate is the harsher version of the same race. The lead costs more and
            the buyer signs with whoever answers first. That is why we built the real
            estate version.
          </p>
        </div>
      </section>

      <section aria-labelledby="how-we-work-title" className="border-b border-white/5 bg-ink-800" data-id="element-380" data-mp="3dfbvmiiriwkd">
        <div className="mx-auto max-w-content px-5 py-20 sm:px-8 sm:py-24" data-id="element-381" data-mp="39bdrtuihsxkz">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand" data-id="element-382" data-mp="1137azuksvwnn">
            Terms
          </p>
          <h2 id="how-we-work-title" className="mt-5 max-w-2xl font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-383" data-mp="15w0fqig9r4zq">
            How we work.
          </h2>

          <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-384" data-mp="w2jjl7sntdsw">
            {howWeWorkItems.map(item => <li key={item} className="flex items-start gap-3 rounded-xl border border-white/10 bg-ink-700 p-6 text-sm font-medium text-white/80" data-id="element-385" data-mp="1otbhc9pdiaic">
                <CheckIcon className="mt-0.5 h-4 w-4 flex-none text-brand" aria-hidden="true" data-id="element-386" data-mp="1ev41ju6tlyhx" />
                <span className="leading-relaxed" data-id="element-387" data-mp="3vig9vgkydics">{item}</span>
              </li>)}
          </ul>
        </div>
      </section>

      <CtaBand title="Talk to the people who built the system." body="Fifteen minutes on your lead sources, your response times, and what a fully answered pipeline is worth to your team." data-id="element-388" data-mp="3s23mzp53xzwy" />
    </>;
}